import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Button, Table, Pagination, Checkbox, Dropdown,
  Menu, Input, Space, Divider, Modal, DatePicker, Select
} from "antd";
import { SettingOutlined, RocketOutlined, SearchOutlined, FilterOutlined, CalendarOutlined, ClearOutlined, CheckSquareOutlined, BorderOutlined } from '@ant-design/icons';
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import * as S from "./styles";
import PeriodoRangePicker from "../PeriodoRangePicker";
import api from "../../service/api";
import { registerLocale } from "react-datepicker";
import { ptBR as datePtBR } from "date-fns/locale";
import ptBR from "antd/lib/locale/pt_BR";
import { format } from "date-fns";
import moment from "moment";
import type { InputRef } from "antd";
import { ConfigProvider } from "antd";
import { Download, Calculator } from "@phosphor-icons/react/dist/ssr";

interface DataItem {
  id: number; empresa: string; cnpj: string; adquirente: string;
  valorBruto: string; desconto: string; taxa: string | null; valorLiquido: string;
  numResumoRv: string; nsucv: string; codigoDeAutorizacao: string;
  numeroDoCartao: string; numeroTerminal: string; bandeiraDoComprovante: string | null;
  tipo: string | null; dataVenda: string; previsaoPgtoParcela: string | null;
  dataPgtoParcela: string; numParcela: string; numTotalParcela: string;
  statusPgto: string; banco: string; agencia: string; conta: string;
  categoria: string | null; antecipado: string; loteUnico: string;
  tid: string;
  horaVenda: string;
  parcelas?: DataItem[];
}

const formatCurrency = (value?: string | number) => {
  const safeValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : (value || 0);
  return (
    <div style={{ textAlign: "right", whiteSpace: 'nowrap' }}>
      {safeValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
    </div>
  );
};

const formatPercent = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>—</div>;
  }

  const safeValue = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;

  if (Number.isNaN(safeValue)) {
    return <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>—</div>;
  }

  return (
    <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
      {safeValue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%
    </div>
  );
};

// Aceita so digitos e UMA virgula (percentual pt-BR, ex.: "10,05"). Ponto vira virgula.
const sanitizarTaxa = (valor: string) => {
  let out = (valor ?? "").replace(/\./g, ",").replace(/[^\d,]/g, "");
  const i = out.indexOf(",");
  if (i !== -1) out = out.slice(0, i + 1) + out.slice(i + 1).replace(/,/g, "");
  return out;
};

// Input da coluna NOVA TAXA com estado LOCAL: digitar nao re-renderiza a tabela toda (sem lentidao).
// O valor e reportado para uma ref no pai (lido no "Simular"); Enter pula para a proxima linha.
const NovaTaxaInput = ({
  rowId,
  valorInicial,
  onChangeValor,
  onEnter,
}: {
  rowId: number;
  valorInicial: string;
  onChangeValor: (rowId: number, valor: string) => void;
  onEnter: (rowId: number) => void;
}) => {
  const [valor, setValor] = useState(valorInicial ?? "");
  return (
    <Input
      id={`nova-taxa-input-${rowId}`}
      size="small"
      inputMode="decimal"
      value={valor}
      placeholder="%"
      style={{ width: 90, textAlign: "right" }}
      onChange={(e) => {
        const v = sanitizarTaxa(e.target.value);
        setValor(v);
        onChangeValor(rowId, v);
      }}
      onPressEnter={() => onEnter(rowId)}
    />
  );
};

// Input EDITAVEL da coluna TAXA CONTRATADA. Estado local (digitar nao re-renderiza a tabela toda) e
// sincroniza quando o valor externo muda (ex.: botao "→" copiando a cobrada). Commita numero no pai.
// Enter foca o proximo input da coluna na ordem VISIVEL (DOM), robusto a filtro/ordenacao.
const focarProximaTaxaContratada = (rowId: number) => {
  const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('[id^="taxa-contratada-input-"]'));
  const atual = document.getElementById(`taxa-contratada-input-${rowId}`) as HTMLInputElement | null;
  const pos = atual ? inputs.indexOf(atual) : -1;
  const proximo = pos >= 0 ? inputs[pos + 1] : null;
  proximo?.focus();
  proximo?.select();
};

const TaxaContratadaInput = ({
  rowId,
  valorExterno,
  onCommit,
}: {
  rowId: number;
  valorExterno: number | null;
  onCommit: (num: number | null) => void;
}) => {
  const [txt, setTxt] = useState(valorExterno == null ? "" : String(valorExterno).replace(".", ","));
  const skipSync = React.useRef(false);

  React.useEffect(() => {
    if (skipSync.current) { skipSync.current = false; return; }
    setTxt(valorExterno == null ? "" : String(valorExterno).replace(".", ","));
  }, [valorExterno]);

  return (
    <Input
      id={`taxa-contratada-input-${rowId}`}
      size="small"
      inputMode="decimal"
      value={txt}
      placeholder="—"
      suffix="%"
      style={{ width: 110, textAlign: "right" }}
      onChange={(e) => {
        const v = sanitizarTaxa(e.target.value);
        skipSync.current = true;
        setTxt(v);
        const limpo = v.replace(",", ".");
        onCommit(limpo === "" || !Number.isFinite(Number(limpo)) ? null : Number(limpo));
      }}
      onPressEnter={() => focarProximaTaxaContratada(rowId)}
    />
  );
};

const formatCNPJ = (cnpj: string) =>
  cnpj ? cnpj.replace(/\D/g, "").replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "";

const parseBrDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const [day, month, year] = value.split('/');
  if (!day || !month || !year) return null;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(parsed.getTime()) ? null : parsed;
};

const calcPrazoDias = (dataVenda?: string | null, previsao?: string | null) => {
  const venda = parseBrDate(dataVenda);
  const prev = parseBrDate(previsao);
  if (!venda || !prev) return "-";
  const dias = Math.round((prev.getTime() - venda.getTime()) / (1000 * 60 * 60 * 24));
  return (
    <span style={{ color: dias < 0 ? "#e74c3c" : undefined }}>
      {dias} dia{Math.abs(dias) === 1 ? '' : 's'}
    </span>
  );
};

const formatSummaryCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatSummaryCount = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("pt-BR");
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = parseFloat(value.replace(".", "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getDateRangeFromFilter = (value: any): [Date | null, Date | null] => {
  if (!value) return [null, null];

  // Antd filterDropdown usually stores range as [[start, end]].
  if (Array.isArray(value) && Array.isArray(value[0])) {
    return [value[0][0] || null, value[0][1] || null];
  }

  // Fallback: [start, end]
  if (Array.isArray(value) && value.length === 2) {
    return [value[0] || null, value[1] || null];
  }

  return [null, null];
};

const TabelaConciliadorFinanceira: React.FC<{ currentUser: any }> = ({ currentUser }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [filteredData, setFilteredData] = useState<DataItem[]>([]);
  const [summaryTotals, setSummaryTotals] = useState({
    valorBruto: null as number | null,
    totalDesconto: null as number | null,
    totalLiquido: null as number | null,
    totalPago: null as number | null,
    diferenca: null as number | null,
    totalAtraso: null as number | null,
    totalAReceber: null as number | null,
  });
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>(() => {
    // DATA VENDA ja inicia filtrada nos ultimos 30 dias (29 dias atras -> hoje).
    const fim = new Date(); fim.setHours(23, 59, 59, 999);
    const ini = new Date(); ini.setDate(ini.getDate() - 29); ini.setHours(0, 0, 0, 0);
    return { dataVenda: [[ini, fim]] };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [grupoDoCliente, setGrupoDoCliente] = useState<string | null>(null);
  const [tableKey, setTableKey] = useState(Date.now());
  const [somenteAtraso, setSomenteAtraso] = useState(false);
  const searchInputRef = React.useRef<InputRef | null>(null);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [isSimuladorVisible, setIsSimuladorVisible] = useState(false);
  const [simuladorRange, setSimuladorRange] = useState<[any, any] | null>(null);
  const [simuladorOperadoras, setSimuladorOperadoras] = useState<string[]>([]);
  const [simuladorEmpresas, setSimuladorEmpresas] = useState<string[]>([]);
  const [simuladorBandeiras, setSimuladorBandeiras] = useState<string[]>([]);
  const [simuladorResultado, setSimuladorResultado] = useState<any[]>([]);
  const [isSimulando, setIsSimulando] = useState(false);
  // Valores digitados em NOVA TAXA ficam numa ref (nao re-renderiza a tabela ao digitar). O key remonta
  // os inputs (limpa) quando uma nova simulacao e executada.
  const novasTaxasRef = React.useRef<Record<number, string>>({});
  const [simuladorInputKey, setSimuladorInputKey] = useState(0);
  const [simulados, setSimulados] = useState<Record<number, { novoLiquido: number; diferenca: number }>>({});
  // Filtros ativos da tabela do simulador (bandeira/tipoVenda) — usados p/ cruzar as opções em cascata.
  const [simColumnFilters, setSimColumnFilters] = useState<Record<string, any>>({});
  const [downloadRangeVenda, setDownloadRangeVenda] = useState<[any, any] | null>(null);
  const [downloadRangePgto, setDownloadRangePgto] = useState<[any, any] | null>(null);
  // "Previsão de Pagamento": janela futura (ex.: amanhã -> +30 dias). Pré-preenchida ao escolher o tipo.
  const [downloadRangePrevisao, setDownloadRangePrevisao] = useState<[any, any] | null>(null);

  // ---- "Taxas Contratada": taxas (ConciliadorVendas) com Contratada editavel + filtros por coluna ----
  const [isContratadaVisible, setIsContratadaVisible] = useState(false);
  const [contratadaResultado, setContratadaResultado] = useState<any[]>([]);
  const [isContratando, setIsContratando] = useState(false);
  const [conColumnFilters, setConColumnFilters] = useState<Record<string, any>>({});
  const { RangePicker } = DatePicker;
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState<"venda" | "pagamento" | "previsao" | "atraso">("venda");

  // Janela padrão de Previsão de Pagamento: de amanhã até +30 dias (moment, formato do RangePicker do antd).
  const previsaoRangePadrao = (): [any, any] => [
    moment().add(1, "day").startOf("day"),
    moment().add(30, "days").endOf("day"),
  ];
  const { Option } = Select;
  const [vendaSelecionada, setVendaSelecionada] = useState<DataItem | null>(null);

  const formatSafeDate = (date: any): string | null => {
    if (!date) return null;

    const d = typeof date.toDate === 'function' ? date.toDate() : date;
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;

    return format(d, "yyyy-MM-dd");
  };

  const [options, setOptions] = useState({
    empresas: [] as string[],
    cnpjs: [] as string[],
    adquirentes: [] as string[],
    status: [] as string[],
    bancos: [] as string[],
    agencias: [] as string[],
    contas: [] as string[],
    bandeiras: [] as string[],
    tipos: [] as string[],
    categorias: [] as string[],
    antecipadoOpcoes: [] as string[],
    loteUnicoOpcoes: [] as string[],
    previsaoPgtoParcelas: [] as string[],
    numeroParcelas: [] as string[],
    tids: [] as string[],
    horaVendas: [] as string[]
  });
  const baseOptionsSet = React.useRef(false);
  const [baseOptions, setBaseOptions] = useState({
    empresas: [] as string[],
    cnpjs: [] as string[],
    adquirentes: [] as string[],
    status: [] as string[],
    bancos: [] as string[],
    agencias: [] as string[],
    contas: [] as string[],
    bandeiras: [] as string[],
    tipos: [] as string[],
    antecipadoOpcoes: [] as string[],
    numeroParcelas: [] as string[],
  });

  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "empresa", "cnpj", "adquirente", "bandeiraDoComprovante", "tipo", "numTotalParcela",
    "valorBruto", "desconto", "taxa", "valorLiquido", "dataVenda", "horaVenda", "previsaoPgtoParcela",
    "dataPgtoParcela", "prazo", "statusPgto", "antecipado", "numResumoRv", "nsucv",
    "codigoDeAutorizacao", "numeroDoCartao", "numeroTerminal", "banco", "agencia", "conta",
    "loteUnico", "tid",
  ]);

  const fetchData = useCallback(async (page = 1) => {
    if (!grupoDoCliente) return;
    setIsLoading(true);
    try {
      const params: any = {
        NomeDoGrupo: grupoDoCliente,
        StartIndex: page,
        PageSize: 50
      };

      if (somenteAtraso) params.SomenteAtraso = true;

      Object.keys(columnFilters).forEach(key => {
        const val = columnFilters[key];
        if (!val || (Array.isArray(val) && val.length === 0)) return;

        if (key === "dataVenda") {
          const [startDate, endDate] = getDateRangeFromFilter(val);
          if (startDate) params.DataVendaInicio = format(startDate, "yyyy-MM-dd");
          if (endDate) params.DataVendaFim = format(endDate, "yyyy-MM-dd");
          return;
        }

        if (key === "dataPgtoParcela") {
          const [startDate, endDate] = getDateRangeFromFilter(val);
          if (startDate) params.DataPgtoInicio = format(startDate, "yyyy-MM-dd");
          if (endDate) params.DataPgtoFim = format(endDate, "yyyy-MM-dd");
          return;
        }

        if (key === "previsaoPgtoParcela") {
          const [startDate, endDate] = getDateRangeFromFilter(val);
          const previsaoInicio = startDate ? format(startDate, "yyyy-MM-dd") : null;
          const previsaoFim = endDate ? format(endDate, "yyyy-MM-dd") : null;

          // DTO expects a single field: PrevisaoPgtoParcelas.
          if (previsaoInicio && previsaoFim) {
            params.PrevisaoPgtoParcelas = `${previsaoInicio},${previsaoFim}`;
          } else if (previsaoInicio) {
            params.PrevisaoPgtoParcelas = previsaoInicio;
          } else if (previsaoFim) {
            params.PrevisaoPgtoParcelas = previsaoFim;
          }
          return;
        }

        let cSharpKey = key.charAt(0).toUpperCase() + key.slice(1);

        if (key === "previsaoPgtoParcela") cSharpKey = "PrevisaoPgtoParcelas";
        if (key === "numTotalParcela") cSharpKey = "NumeroParcelas";
        if (key === "tid") cSharpKey = "TIDs";
        if (key === "horaVenda") cSharpKey = "HoraVendas";

        let filterValue = Array.isArray(val) ? val.join(',') : val;

        if (["valorBruto", "desconto", "valorLiquido", "taxa"].includes(key) && typeof filterValue === 'string') {
          filterValue = filterValue.replace(/[R$\s.%]/g, '').replace(',', '.');
        }

        if (key === "numeroDoCartao" && typeof filterValue === 'string') {
          filterValue = filterValue.replace(/\D/g, '').slice(-4);
        }

        params[cSharpKey] = filterValue;
      });

      console.log("[DEBUG] Params enviados à API:", params);
      const response = await api.get("/ConciliadorVendas/get-all-conciliador-vendas", { params });

      if (response.data?.success && response.data.data) {
        const d = response.data.data;
        const normalizedResult = (d.result || []).map((row: any) => ({
          ...row,
          dataPgtoParcela:
            row?.dataPgtoParcela ??
            row?.dataPgto ??
            row?.dataPagamentoParcela ??
            row?.DataPgtoParcela ??
            row?.DataPgto ??
            row?.DataPagamentoParcela ??
            "",
          previsaoPgtoParcela:
            row?.previsaoPgtoParcela ??
            row?.previsaoPgto ??
            row?.previsaoPagamentoParcela ??
            row?.dataPrevisaoPgto ??
            row?.dataPrevisaoPagamento ??
            row?.PrevisaoPgtoParcela ??
            row?.PrevisaoPgto ??
            row?.PrevisaoPagamentoParcela ??
            row?.DataPrevisaoPgto ??
            row?.DataPrevisaoPagamento ??
            "",
        }));

        const hasDatePgtoFilter = params.DataPgtoInicio || params.DataPgtoFim;
        const hasDateVendaFilter = params.DataVendaInicio || params.DataVendaFim;
        const hasPrevisaoFilter = params.PrevisaoPgtoParcelas;

        const postFiltered = normalizedResult.filter((row: any) => {
          if (hasDatePgtoFilter && !row.dataPgtoParcela) return false;
          if (hasDateVendaFilter && !row.dataVenda) return false;
          if (hasPrevisaoFilter && !row.previsaoPgtoParcela) return false;
          return true;
        });

        setFilteredData(postFiltered);
        setTotalItems(d.totalCount || 0);
        setCurrentPage(page);
        setSummaryTotals({
          valorBruto: toNullableNumber(d.totalBruto ?? d.valorBruto ?? d.ValorBruto),
          totalDesconto: toNullableNumber(d.totalDesconto ?? d.totalDescontos ?? d.TotalDesconto ?? d.desconto ?? d.Desconto),
          totalLiquido: toNullableNumber(d.totalLiquido ?? d.TotalLiquido),
          totalPago: toNullableNumber(d.totalPago ?? d.TotalPago),
          diferenca: toNullableNumber(d.diferenca ?? d.Diferenca),
          totalAtraso: toNullableNumber(d.totalAtraso ?? d.TotalAtraso),
          totalAReceber: toNullableNumber(d.totalAReceber ?? d.TotalAReceber),
        });

        // CORREÇÃO AQUI: Mapeando d.tiDs (como vem do back) para options.tids
        const newOptions = {
          empresas: d.empresas || [],
          cnpjs: d.cnpjs || [],
          adquirentes: d.adquirentes || [],
          status: d.statusOpcoes || [],
          bancos: d.bancos || [],
          agencias: d.agencias || [],
          contas: d.contas || [],
          bandeiras: d.bandeiras || [],
          tipos: d.tipos || [],
          categorias: d.categorias || [],
          antecipadoOpcoes: d.antecipadoOpcoes || [],
          loteUnicoOpcoes: d.loteUnicoOpcoes || [],
          previsaoPgtoParcelas: d.previsaoPgtoParcelas || [],
          numeroParcelas: d.numeroParcelas || [],
          tids: d.tiDs || [],
          horaVendas: d.horaVendas || []
        };
        setOptions(newOptions);
        if (!baseOptionsSet.current) {
          setBaseOptions({
            empresas: newOptions.empresas,
            cnpjs: newOptions.cnpjs,
            adquirentes: newOptions.adquirentes,
            status: newOptions.status,
            bancos: newOptions.bancos,
            agencias: newOptions.agencias,
            contas: newOptions.contas,
            bandeiras: newOptions.bandeiras,
            tipos: newOptions.tipos,
            antecipadoOpcoes: newOptions.antecipadoOpcoes,
            numeroParcelas: newOptions.numeroParcelas,
          });
          baseOptionsSet.current = true;
        }
      }
    } catch (error) {
      toast.error("Erro ao carregar dados.");
    } finally {
      setIsLoading(false);
    }
  }, [grupoDoCliente, columnFilters, somenteAtraso]);

  useEffect(() => {
    const cliente = localStorage.getItem("cliente");
    if (cliente) {
      const obj = JSON.parse(cliente);
      if (obj?.nomeDoGrupo) setGrupoDoCliente(obj.nomeDoGrupo);
    }
  }, []);

  useEffect(() => {
    if (grupoDoCliente) fetchData(1);
  }, [grupoDoCliente, columnFilters, somenteAtraso, fetchData]);

  const getColumnSearchProps = (dataIndex: string, title: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Buscar ${title}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">
            Buscar
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
          >
            Limpar
          </Button>
        </Space>
      </div>
    ),

    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ fontSize: 18, color: filtered ? '#1890ff' : undefined }} />
    ),

    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  });

  const handleDownload = async () => {
    if (!grupoDoCliente || isDownloading) return; // Proteção extra no início da função

    if (downloadType !== "atraso" && !downloadRangeVenda && !downloadRangePgto && !downloadRangePrevisao) {
      toast.warning("Por favor, selecione ao menos um período para o download.");
      return;
    }

    try {
      setIsDownloading(true);

      const formatSafeDate = (date: any): string | null => {
        if (!date) return null;
        const d = typeof date.toDate === 'function' ? date.toDate() : date;
        return format(d, "yyyy-MM-dd");
      };

      const isAtraso = downloadType === "atraso";

      const payload = {
        Grupo: grupoDoCliente,
        DataVendaInicio: isAtraso ? null : formatSafeDate(downloadRangeVenda?.[0]),
        DataVendaFim: isAtraso ? null : formatSafeDate(downloadRangeVenda?.[1]),
        DataPagamentoInicio: isAtraso ? null : formatSafeDate(downloadRangePgto?.[0]),
        DataPagamentoFim: isAtraso ? null : formatSafeDate(downloadRangePgto?.[1]),
        DataPrevisaoInicio: isAtraso ? null : formatSafeDate(downloadRangePrevisao?.[0]),
        DataPrevisaoFim: isAtraso ? null : formatSafeDate(downloadRangePrevisao?.[1]),
        SomenteAtraso: isAtraso,
      };

      const response = await api.post("/ConciliadorVendas/download-csv-conciliador-vendas", payload, {
        responseType: 'blob',
      });

      // Lógica do Nome conforme solicitado: Conciliacao_GRUPO_vendas_DATA_DATA (ou _atraso, sem período)
      const grupoLimpo = grupoDoCliente.replace(/\s+/g, '_');
      let fileName: string;

      if (isAtraso) {
        fileName = `Conciliacao_${grupoLimpo}_atraso.csv`;
      } else {
        const isVenda = !!(downloadRangeVenda?.[0] || downloadRangeVenda?.[1]);
        const isPrevisao = !!(downloadRangePrevisao?.[0] || downloadRangePrevisao?.[1]);
        const tipoSufixo = isVenda ? "vendas" : isPrevisao ? "previsao" : "pagamentos";
        const rangeAtivo = isVenda ? downloadRangeVenda : isPrevisao ? downloadRangePrevisao : downloadRangePgto;

        const strIni = rangeAtivo?.[0] ? format(rangeAtivo[0].toDate ? rangeAtivo[0].toDate() : rangeAtivo[0], "yyyyMMdd") : "inicio";
        const strFim = rangeAtivo?.[1] ? format(rangeAtivo[1].toDate ? rangeAtivo[1].toDate() : rangeAtivo[1], "yyyyMMdd") : "fim";

        fileName = `Conciliacao_${grupoLimpo}_${tipoSufixo}_${strIni}_${strFim}.csv`;
      }

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
      setIsDownloadModalVisible(false);
      setDownloadRangeVenda(null);
      setDownloadRangePgto(null);
      setDownloadRangePrevisao(null);
      toast.success("Download concluído!");

    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar arquivo para download.");
    } finally {
      setIsDownloading(false); // Libera os botões
    }
  };

  const handleSimular = async () => {
    if (!grupoDoCliente || isSimulando) return;

    try {
      setIsSimulando(true);

      const payload = {
        Grupo: grupoDoCliente,
        DataInicio: formatSafeDate(simuladorRange?.[0]),
        DataFim: formatSafeDate(simuladorRange?.[1]),
        Operadoras: simuladorOperadoras.join(','),
        Empresas: simuladorEmpresas.join(','),
        Bandeiras: simuladorBandeiras.join(','),
      };

      const response = await api.post('/ConciliadorVendas/simular-taxas', payload);
      // __rowId: identidade ESTÁVEL por linha (não muda com filtro/ordenação da tabela).
      // Antes a Nova Taxa era indexada pela POSIÇÃO renderizada (index do AntD), então filtrar
      // reatribuía o valor digitado à linha errada. Agora tudo é amarrado a este id.
      const dados = (response.data?.data ?? []).map((row: any, i: number) => ({ ...row, __rowId: i }));
      setSimuladorResultado(dados);
      novasTaxasRef.current = {};
      setSimuladorInputKey((k) => k + 1);
      setSimulados({});
      setSimColumnFilters({});
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar a simulação.');
    } finally {
      setIsSimulando(false);
    }
  };

  const handleSimularNovaTaxa = () => {
    const resultado: Record<number, { novoLiquido: number; diferenca: number }> = {};
    simuladorResultado.forEach((row: any) => {
      const id = row.__rowId;
      const raw = novasTaxasRef.current[id];
      if (raw === undefined || raw === '') return;
      const taxa = parseFloat(String(raw).replace(',', '.'));
      if (!Number.isFinite(taxa)) return;
      const bruto = Number(row.valorBruto) || 0;
      const novoLiquido = bruto * (1 - taxa / 100);
      const diferenca = novoLiquido - (Number(row.valorLiquido) || 0);
      resultado[id] = { novoLiquido, diferenca };
    });
    setSimulados(resultado);
  };

  // Baixa o CSV do Simulador com o que esta na tela (inclui Nova Taxa/Novo Líquido/Diferença digitados).
  const handleDownloadCsvSimulador = async () => {
    if (simuladorResultado.length === 0) return;
    try {
      const num = (v: unknown) =>
        (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      const linhas = simuladorResultado.map((row: any) => {
        const id = row.__rowId;
        return {
          operadora: row?.operadora ?? '',
          bandeira: row?.bandeira ?? '',
          tipoVenda: row?.tipoVenda ?? '',
          valorBruto: num(row?.valorBruto),
          valorLiquido: num(row?.valorLiquido),
          taxaAtual: num(row?.taxaAtual),
          novaTaxa: novasTaxasRef.current[id] ?? '',
          novoLiquido: simulados[id] ? num(simulados[id].novoLiquido) : '',
          diferenca: simulados[id] ? num(simulados[id].diferenca) : '',
        };
      });

      const response = await api.post(
        '/ConciliadorVendas/simular-taxas/csv',
        { grupo: grupoDoCliente, linhas, totalDiferenca: num(totalDiferencaSimulada) },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Simulador_Taxas.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar o CSV.');
    }
  };

  // Total geral da coluna DIFERENÇA (soma de todas as diferenças simuladas).
  const totalDiferencaSimulada = Object.values(simulados)
    .reduce((acc, s) => acc + (Number(s?.diferenca) || 0), 0);

  const getColumnCheckboxProps = (opts: string[]) => {
    const sorted = [...opts].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, minWidth: 160 }}>
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
            {sorted.map(opt => (
              <div
                key={opt}
                style={{ padding: '4px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => {
                  const current: string[] = Array.isArray(selectedKeys) ? selectedKeys : [];
                  const next = current.includes(opt)
                    ? current.filter(k => k !== opt)
                    : [...current, opt];
                  setSelectedKeys(next);
                }}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={Array.isArray(selectedKeys) && selectedKeys.includes(opt)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13 }}>{opt}</span>
              </div>
            ))}
          </div>
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>OK</Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Limpar</Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ fontSize: 18, color: filtered ? "#1677ff" : undefined }} />
      ),
    };
  };

  // Cascata: as opções de cada filtro refletem o que sobra pela seleção do OUTRO filtro.
  // Ex.: ao escolher uma bandeira, TIPO DE VENDA só lista os tipos existentes naquela bandeira.
  const bandeirasSelecionadas: string[] = (simColumnFilters?.bandeira as string[]) ?? [];
  const tiposSelecionados: string[] = (simColumnFilters?.tipoVenda as string[]) ?? [];

  const bandeirasSimulador = Array.from(new Set(
    simuladorResultado
      .filter((r: any) => tiposSelecionados.length === 0 || tiposSelecionados.includes(r?.tipoVenda))
      .map((r: any) => r?.bandeira)
      .filter(Boolean) as string[]
  ));

  const tiposVendaSimulador = Array.from(new Set(
    simuladorResultado
      .filter((r: any) => bandeirasSelecionadas.length === 0 || bandeirasSelecionadas.includes(r?.bandeira))
      .map((r: any) => r?.tipoVenda)
      .filter(Boolean) as string[]
  ));

  const simuladorColumns = [
    { key: 'operadora', title: 'OPERADORA', dataIndex: 'operadora', width: 140 },
    {
      key: 'bandeira',
      title: 'BANDEIRA',
      dataIndex: 'bandeira',
      width: 130,
      ...getColumnCheckboxProps(bandeirasSimulador),
      filteredValue: (simColumnFilters?.bandeira as any) ?? null,
      onFilter: (value: any, record: any) => record?.bandeira === value,
    },
    {
      key: 'tipoVenda',
      title: 'TIPO DE VENDA',
      dataIndex: 'tipoVenda',
      width: 150,
      ...getColumnCheckboxProps(tiposVendaSimulador),
      filteredValue: (simColumnFilters?.tipoVenda as any) ?? null,
      onFilter: (value: any, record: any) => record?.tipoVenda === value,
    },
    { key: 'valorBruto', title: 'VLR BRUTO', dataIndex: 'valorBruto', width: 130, render: formatCurrency },
    { key: 'valorLiquido', title: 'VLR LÍQUIDO', dataIndex: 'valorLiquido', width: 130, render: formatCurrency },
    { key: 'taxaAtual', title: 'TAXA ATUAL', dataIndex: 'taxaAtual', width: 110, render: formatPercent },
    {
      key: 'novaTaxa',
      title: 'NOVA TAXA',
      width: 120,
      render: (_: unknown, record: any) => (
        <NovaTaxaInput
          key={`${simuladorInputKey}-${record.__rowId}`}
          rowId={record.__rowId}
          valorInicial={novasTaxasRef.current[record.__rowId] ?? ''}
          onChangeValor={(id, v) => { novasTaxasRef.current[id] = v; }}
          onEnter={(id) => {
            // Foca o próximo input na ordem VISÍVEL (DOM), robusto a filtro/ordenação.
            const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('[id^="nova-taxa-input-"]'));
            const atual = document.getElementById(`nova-taxa-input-${id}`) as HTMLInputElement | null;
            const pos = atual ? inputs.indexOf(atual) : -1;
            const proximo = pos >= 0 ? inputs[pos + 1] : null;
            proximo?.focus();
            proximo?.select();
          }}
        />
      ),
    },
    {
      key: 'novoLiquido',
      title: 'NOVO LÍQUIDO',
      width: 130,
      render: (_: unknown, record: any) =>
        simulados[record.__rowId] ? formatCurrency(simulados[record.__rowId].novoLiquido)
          : <div style={{ textAlign: 'right' }}>—</div>,
    },
    {
      key: 'diferenca',
      title: 'DIFERENÇA',
      width: 130,
      render: (_: unknown, record: any) =>
        simulados[record.__rowId]
          ? <div style={{ textAlign: 'right', color: simulados[record.__rowId].diferenca >= 0 ? '#27ae60' : '#e74c3c' }}>
              {simulados[record.__rowId].diferenca.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
          : <div style={{ textAlign: 'right' }}>—</div>,
    },
  ];

  // ======== "Taxas Contratada": busca as taxas cobradas (ConciliadorVendas) e colunas read-only ========
  const handleCarregarTaxasContratadas = async () => {
    if (!grupoDoCliente) return;
    try {
      setIsContratando(true);
      const response = await api.get('/ConciliadorVendas/get-taxas-contratadas', {
        params: { nomeDoGrupo: grupoDoCliente },
      });
      // __rowId: id ESTÁVEL por linha (edição/→ não dependem do índice, que muda com os filtros).
      const dados = (response.data?.data ?? []).map((r: any, i: number) => ({ ...r, __rowId: i }));
      setContratadaResultado(dados);
      setConColumnFilters({});
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar as taxas contratadas.');
    } finally {
      setIsContratando(false);
    }
  };

  // Salva as taxas contratadas preenchidas (TaxaContratada) no CadastroTaxas (upsert por combinacao).
  const handleSalvarTaxasContratadas = async () => {
    if (isContratando) return;

    // Só bloqueia o que está VAZIO ("—"); zero é permitido (pode haver taxa 0).
    const invalidas = contratadaResultado.filter((r: any) => r?.taxaContratada == null);
    if (invalidas.length > 0) {
      toast.error('Não é possível salvar: há Taxa Contratada vazia (—). Preencha todas as linhas (0 é permitido).');
      return;
    }

    const itens = contratadaResultado.map((r: any) => ({
      empresa: r.empresa,
      cnpj: r.cnpj,
      operadora: r.operadora,
      bandeira: r.bandeira,
      tipoVenda: r.tipoVenda,
      taxaContratada: r.taxaContratada,
    }));

    try {
      setIsContratando(true);
      await api.post('/CadastroTaxas/salvar-taxas-contratadas', { grupo: grupoDoCliente, itens });
      toast.success('Taxas contratadas salvas com sucesso.');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar as taxas contratadas.');
    } finally {
      setIsContratando(false);
    }
  };

  // Copia a Taxa Cobrada -> Taxa Contratada da linha (por id estavel).
  const moverTaxaContratada = (rowId: number) => {
    setContratadaResultado((prev) =>
      prev.map((r) => (r.__rowId === rowId ? { ...r, taxaContratada: r.taxaCobrada } : r))
    );
  };

  // Edicao manual da Taxa Contratada (por id estavel).
  const editarTaxaContratada = (rowId: number, num: number | null) => {
    setContratadaResultado((prev) =>
      prev.map((r) => (r.__rowId === rowId ? { ...r, taxaContratada: num } : r))
    );
  };

  // Copia a Taxa Cobrada -> Taxa Contratada de TODAS as linhas de uma vez.
  const moverTodasTaxasContratadas = () => {
    setContratadaResultado((prev) => prev.map((r) => ({ ...r, taxaContratada: r.taxaCobrada })));
  };

  const distinctCon = (campo: string): string[] =>
    Array.from(new Set(contratadaResultado.map((r: any) => r?.[campo]).filter(Boolean))) as string[];

  const filtroCol = (campo: string) => ({
    ...getColumnCheckboxProps(distinctCon(campo)),
    filteredValue: (conColumnFilters?.[campo] as any) ?? null,
    onFilter: (value: any, record: any) => record?.[campo] === value,
  });

  const contratadaColumns = [
    { key: 'empresa', title: 'EMPRESA', dataIndex: 'empresa', width: 190, ...filtroCol('empresa') },
    { key: 'cnpj', title: 'CNPJ', dataIndex: 'cnpj', width: 160, ...filtroCol('cnpj') },
    { key: 'operadora', title: 'OPERADORA', dataIndex: 'operadora', width: 140, ...filtroCol('operadora') },
    { key: 'bandeira', title: 'BANDEIRA', dataIndex: 'bandeira', width: 130, ...filtroCol('bandeira') },
    { key: 'tipoVenda', title: 'TIPO DE VENDA', dataIndex: 'tipoVenda', width: 190, ...filtroCol('tipoVenda') },
    { key: 'taxaCobrada', title: 'TAXA COBRADA (%)', dataIndex: 'taxaCobrada', width: 150, render: (v: any) => (v == null ? '—' : formatPercent(v)) },
    {
      key: 'mover',
      title: (
        <Button
          size="small"
          title="Copiar TODAS: Taxa Cobrada → Taxa Contratada"
          onClick={() =>
            Modal.confirm({
              title: 'Confirma aplicar taxa cobrada em todas as taxas cadastradas?',
              okText: 'Confirmar',
              cancelText: 'Cancelar',
              centered: true,
              onOk: moverTodasTaxasContratadas,
            })
          }
        >
          →
        </Button>
      ),
      width: 60,
      align: 'center' as const,
      render: (_: unknown, record: any) => (
        <Button
          size="small"
          title="Copiar Taxa Cobrada → Taxa Contratada"
          onClick={() => moverTaxaContratada(record.__rowId)}
        >
          →
        </Button>
      ),
    },
    {
      key: 'taxaContratada',
      title: 'TAXA CONTRATADA (%)',
      dataIndex: 'taxaContratada',
      width: 180,
      render: (v: any, record: any) => (
        <TaxaContratadaInput
          rowId={record.__rowId}
          valorExterno={v == null ? null : Number(v)}
          onCommit={(num) => editarTaxaContratada(record.__rowId, num)}
        />
      ),
    },
  ];
  // ======== fim "Taxas Contratada" ========

  const getColumnDateProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div
        style={{
          padding: 4,
          width: "fit-content",
          maxWidth: 250
        }}
      >
        <ReactDatePicker
          selectsRange
          inline
          startDate={selectedKeys[0]?.[0] || null}
          endDate={selectedKeys[0]?.[1] || null}
          onChange={(update: [Date | null, Date | null]) => {
            const [start, end] = update || [null, null];
            setSelectedKeys(start ? [[start, end ?? null]] : []);
          }}
          locale="pt-BR"
        />

        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <Button
            type="primary"
            size="small"
            style={{ flex: 1 }}
            onClick={() => {
              const [start, end] = selectedKeys[0] || [null, null];
              if (start) {
                setSelectedKeys([[start, end ?? start]]);
              }
              confirm();
            }}
          >
            Buscar
          </Button>
          <Button
            size="small"
            danger
            style={{ flex: 1 }}
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
          >
            Limpar
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined
        style={{ fontSize: 18, color: filtered ? "#1890ff" : undefined }}
      />
    ),
  });

  const isParcelado = (record: DataItem) =>
    Number(record.numTotalParcela) > 1 || (record.parcelas?.length ?? 0) > 1;

  const celulaAbreModal = (record: DataItem) =>
    isParcelado(record)
      ? {
          onClick: () => setVendaSelecionada(record),
          style: { cursor: 'pointer', color: '#1890ff', fontWeight: 600 },
        }
      : {};

  const allColumnsConfig = [
    {
      key: "empresa",
      title: "EMPRESA",
      dataIndex: "empresa",
      width: 180,
      ...getColumnCheckboxProps(baseOptions.empresas),
    },
    {
      key: "cnpj",
      title: "CNPJ",
      dataIndex: "cnpj",
      width: 180,
      render: formatCNPJ,
      ...getColumnCheckboxProps(baseOptions.cnpjs),
    },
    {
      key: "adquirente",
      title: "ADQUIRENTE",
      dataIndex: "adquirente",
      width: 130,
      ...getColumnCheckboxProps(baseOptions.adquirentes),
    },
    {
      key: "bandeiraDoComprovante",
      title: "BANDEIRA",
      dataIndex: "bandeiraDoComprovante",
      width: 150,
      ...getColumnCheckboxProps(baseOptions.bandeiras),
    },
    {
      key: "tipo",
      title: "TIPO",
      dataIndex: "tipo",
      width: 120,
      ...getColumnCheckboxProps(baseOptions.tipos),
    },
    {
      key: "numTotalParcela",
      title: "TOTAL PARCELAS",
      dataIndex: "numTotalParcela",
      width: 120,
      onCell: celulaAbreModal,
      ...getColumnCheckboxProps(baseOptions.numeroParcelas),
    },
    { key: "valorBruto", title: "VLR BRUTO", dataIndex: "valorBruto", width: 120, render: formatCurrency, ...getColumnSearchProps('valorBruto', 'Vlr Bruto') },
    { key: "desconto", title: "DESCONTO", dataIndex: "desconto", width: 110, render: formatCurrency, ...getColumnSearchProps('desconto', 'Desconto') },
    { key: "taxa", title: "TAXA", dataIndex: "taxa", width: 100, render: formatPercent, ...getColumnSearchProps('taxa', 'Taxa') },
    { key: "valorLiquido", title: "VLR LÍQUIDO", dataIndex: "valorLiquido", width: 120, render: formatCurrency, ...getColumnSearchProps('valorLiquido', 'Vlr Líquido') },
    { key: "dataVenda", title: "DATA VENDA", dataIndex: "dataVenda", width: 130, filteredValue: columnFilters.dataVenda ?? null, ...getColumnDateProps('dataVenda') },
    {
      key: "horaVenda",
      title: "HORA VENDA",
      dataIndex: "horaVenda",
      width: 120,
    },
    {
      key: "previsaoPgtoParcela",
      title: "PREVISÃO PGTO",
      dataIndex: "previsaoPgtoParcela",
      width: 140,
      onCell: celulaAbreModal,
      render: (value: string, record: DataItem) => (isParcelado(record) ? 'Visualizar' : value),
      ...getColumnDateProps('previsaoPgtoParcela'),
    },
    { key: "dataPgtoParcela", title: "DATA PGTO", dataIndex: "dataPgtoParcela", width: 130, ...getColumnDateProps('dataPgtoParcela') },
    {
      key: "prazo",
      title: "PRAZO",
      width: 100,
      render: (_: unknown, record: DataItem) => calcPrazoDias(record.dataVenda, record.previsaoPgtoParcela),
    },
    {
      key: "statusPgto",
      title: "STATUS",
      dataIndex: "statusPgto",
      width: 150,
      onCell: celulaAbreModal,
      ...getColumnCheckboxProps(baseOptions.status),
    },
    {
      key: "antecipado",
      title: "ANTECIPADO",
      dataIndex: "antecipado",
      width: 120,
      ...getColumnCheckboxProps(baseOptions.antecipadoOpcoes),
    },
    { key: "numResumoRv", title: "RESUMO RV", dataIndex: "numResumoRv", width: 120, ...getColumnSearchProps('numResumoRv', 'Resumo RV') },
    { key: "nsucv", title: "NSU/CV", dataIndex: "nsucv", width: 120, ...getColumnSearchProps('nsucv', 'NSU/CV') },
    { key: "codigoDeAutorizacao", title: "AUTORIZAÇÃO", dataIndex: "codigoDeAutorizacao", width: 130, ...getColumnSearchProps('codigoDeAutorizacao', 'Autorização') },
    { key: "numeroDoCartao", title: "CARTÃO", dataIndex: "numeroDoCartao", width: 150, ...getColumnSearchProps('numeroDoCartao', 'Cartão') },
    { key: "numeroTerminal", title: "TERMINAL", dataIndex: "numeroTerminal", width: 120, ...getColumnSearchProps('numeroTerminal', 'Terminal') },
    {
      key: "banco",
      title: "BANCO",
      dataIndex: "banco",
      width: 120,
      ...getColumnCheckboxProps(baseOptions.bancos),
    },
    {
      key: "agencia",
      title: "AGÊNCIA",
      dataIndex: "agencia",
      width: 120,
      ...getColumnCheckboxProps(baseOptions.agencias),
    },
    {
      key: "conta",
      title: "CONTA",
      dataIndex: "conta",
      width: 120,
      ...getColumnCheckboxProps(baseOptions.contas),
    },
    {
      key: "loteUnico",
      title: "LOTE ÚNICO",
      dataIndex: "loteUnico",
      width: 120,
      ...getColumnSearchProps('loteUnico', 'Lote Único')
    },
    {
      key: "tid",
      title: "TID",
      dataIndex: "tid",
      width: 150,
      ...getColumnSearchProps('tid', 'TID')
    }
  ];

  const columns = useMemo(() =>
    allColumnsConfig.filter(col => visibleColumns.includes(col.key)),
    [visibleColumns, baseOptions, allColumnsConfig]);

  const parcelaColumns = [
    {
      key: "parcela",
      title: "PARCELA",
      width: 100,
      render: (_: unknown, record: DataItem) =>
        `${record.numParcela ?? '-'}/${vendaSelecionada?.numTotalParcela ?? record.numTotalParcela ?? '-'}`,
    },
    { key: "valorBruto", title: "VLR BRUTO", dataIndex: "valorBruto", width: 120, render: formatCurrency },
    { key: "desconto", title: "DESCONTO", dataIndex: "desconto", width: 110, render: formatCurrency },
    { key: "taxa", title: "TAXA", dataIndex: "taxa", width: 90, render: formatPercent },
    { key: "valorLiquido", title: "VLR LÍQUIDO", dataIndex: "valorLiquido", width: 120, render: formatCurrency },
    { key: "dataVenda", title: "DATA VENDA", dataIndex: "dataVenda", width: 120 },
    { key: "previsaoPgtoParcela", title: "PREVISÃO PGTO", dataIndex: "previsaoPgtoParcela", width: 130 },
    { key: "dataPgtoParcela", title: "DATA PGTO", dataIndex: "dataPgtoParcela", width: 120 },
    {
      key: "prazo",
      title: "PRAZO",
      width: 100,
      render: (_: unknown, record: DataItem) => calcPrazoDias(record.dataVenda, record.previsaoPgtoParcela),
    },
    { key: "statusPgto", title: "STATUS", dataIndex: "statusPgto", width: 140 },
    { key: "antecipado", title: "ANTECIPADO", dataIndex: "antecipado", width: 110 },
    { key: "banco", title: "BANCO", dataIndex: "banco", width: 120 },
    { key: "agencia", title: "AGÊNCIA", dataIndex: "agencia", width: 100 },
    { key: "conta", title: "CONTA", dataIndex: "conta", width: 110 },
    { key: "loteUnico", title: "LOTE ÚNICO", dataIndex: "loteUnico", width: 120 },
    { key: "tid", title: "TID", dataIndex: "tid", width: 150 },
  ];

  const columnsMenu = (
    <Menu style={{ padding: '8px', maxHeight: '450px', overflowY: 'auto', width: '220px' }}>
      <div style={{ padding: '4px 12px', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="link" size="small" onClick={() => setVisibleColumns(allColumnsConfig.map(c => c.key))} icon={<CheckSquareOutlined />}>Todos</Button>
        <Button type="link" size="small" onClick={() => setVisibleColumns([])} icon={<BorderOutlined />} danger>Nenhum</Button>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      {allColumnsConfig.map(col => (
        <Menu.Item key={col.key} onClick={(e: any) => e.domEvent.stopPropagation()}>
          <Checkbox
            checked={visibleColumns.includes(col.key)}
            onChange={() => {
              setVisibleColumns(prev =>
                prev.includes(col.key) ? prev.filter(k => k !== col.key) : [...prev, col.key]
              );
            }}
          >
            {col.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <S.Container>
      <S.DivTable>
        <S.Tools>
          <Button
            danger
            icon={<ClearOutlined />}
            onClick={() => { setColumnFilters({}); setSomenteAtraso(false); setTableKey(Date.now()); }}
            style={{ fontWeight: 'bold' }}
          >
            LIMPAR FILTROS
          </Button>
          <S.RightAlignedButtons>
            {/* <Button
              type="primary"
              icon={<RocketOutlined />}
              onClick={() => fetchData(currentPage)}
              style={{ background: '#27ae60' }}
            >
              ATUALIZAR
            </Button> */}
            <Button
              type="primary"
              icon={<Calculator />}
              onClick={() => setIsSimuladorVisible(true)}
              style={{ background: '#1890ff', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              SIMULADOR DE TAXAS
            </Button>
            <Button
              onClick={() => { setIsContratadaVisible(true); handleCarregarTaxasContratadas(); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              TAXAS CONTRATADA
            </Button>
            <Button
              type="primary"
              icon={<Download />}
              onClick={() => setIsDownloadModalVisible(true)}
              style={{ background: '#27ae60', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              DOWNLOAD CSV
            </Button>
            <Dropdown overlay={columnsMenu} trigger={['click']} placement="bottomRight">
              <Button icon={<SettingOutlined />} title="Colunas adicionais" />
            </Dropdown>
          </S.RightAlignedButtons>
        </S.Tools>

        <Modal
          title="Simulador de Taxas"
          visible={isSimuladorVisible}
          onCancel={() => setIsSimuladorVisible(false)}
          footer={null}
          centered
          width={1320}
          bodyStyle={{ paddingTop: 12 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px 16px',
                padding: '14px 16px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
              }}
            >
              <div>
                <p style={{ marginBottom: '6px' }}><strong>Selecione o período:</strong></p>
                <PeriodoRangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  locale={ptBR.DatePicker}
                  value={simuladorRange}
                  onChange={(dates) => setSimuladorRange(dates as [any, any])}
                  placeholder={['Início', 'Fim']}
                  inputReadOnly
                />
              </div>

              <div>
                <p style={{ marginBottom: '6px' }}><strong>Empresas do grupo:</strong></p>
                <Select
                  mode="multiple"
                  allowClear
                  value={simuladorEmpresas}
                  onChange={setSimuladorEmpresas}
                  placeholder="Todas"
                  maxTagCount="responsive"
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                >
                  {baseOptions.empresas.map((e) => (
                    <Option key={e} value={e}>{e}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <p style={{ marginBottom: '6px' }}><strong>Operadoras:</strong></p>
                <Select
                  mode="multiple"
                  allowClear
                  value={simuladorOperadoras}
                  onChange={setSimuladorOperadoras}
                  placeholder="Todas"
                  maxTagCount="responsive"
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                >
                  {baseOptions.adquirentes.map((a) => (
                    <Option key={a} value={a}>{a}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <p style={{ marginBottom: '6px' }}><strong>Bandeiras:</strong></p>
                <Select
                  mode="multiple"
                  allowClear
                  value={simuladorBandeiras}
                  onChange={setSimuladorBandeiras}
                  placeholder="Todas"
                  maxTagCount="responsive"
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                >
                  {baseOptions.bandeiras.map((b) => (
                    <Option key={b} value={b}>{b}</Option>
                  ))}
                </Select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                type="primary"
                onClick={handleSimular}
                loading={isSimulando}
                disabled={isSimulando}
                style={{ background: '#27ae60', border: 'none' }}
              >
                Executar
              </Button>
            </div>

            <Table
              columns={simuladorColumns}
              dataSource={simuladorResultado}
              loading={isSimulando}
              rowKey={(record: any) => String(record.__rowId)}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content', y: 420 }}
              locale={{ emptyText: 'Selecione os filtros e clique em Executar.' }}
              onChange={(_, filters) => setSimColumnFilters(filters)}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                Total da diferença:{' '}
                <span style={{ color: totalDiferencaSimulada >= 0 ? '#27ae60' : '#e74c3c' }}>
                  {totalDiferencaSimulada.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  onClick={handleDownloadCsvSimulador}
                  disabled={isSimulando || simuladorResultado.length === 0}
                >
                  Download .csv
                </Button>
                <Button
                  type="primary"
                  onClick={handleSimularNovaTaxa}
                  disabled={isSimulando || simuladorResultado.length === 0}
                  style={{ background: '#1890ff', border: 'none' }}
                >
                  Simular
                </Button>
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          title="Taxas Contratada"
          visible={isContratadaVisible}
          onCancel={() => setIsContratadaVisible(false)}
          footer={null}
          centered
          width={1320}
          destroyOnClose
          bodyStyle={{ paddingTop: 12 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Table
              columns={contratadaColumns}
              dataSource={contratadaResultado}
              loading={isContratando}
              rowKey={(record: any) => String(record.__rowId)}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content', y: 460 }}
              locale={{ emptyText: isContratando ? 'Carregando...' : 'Sem taxas contratadas.' }}
              onChange={(_, filters) => setConColumnFilters(filters)}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                onClick={handleSalvarTaxasContratadas}
                loading={isContratando}
                disabled={isContratando || contratadaResultado.length === 0}
                style={{ background: '#27ae60', border: 'none' }}
              >
                Salvar
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          title="Selecionar Período para Download"
          visible={isDownloadModalVisible}
          onOk={handleDownload}
          onCancel={() => {
            if (isDownloading) return;
            setIsDownloadModalVisible(false);
            setDownloadRangeVenda(null);
            setDownloadRangePgto(null);
            setDownloadRangePrevisao(null);
          }}
          okText={isDownloading ? "Baixando..." : "Baixar CSV"}
          cancelText="Cancelar"
          okButtonProps={{
            style: { background: '#27ae60', border: 'none' },
            loading: isDownloading,
            disabled: isDownloading
          }}
          cancelButtonProps={{
            disabled: isDownloading
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <p style={{ marginBottom: '8px' }}><strong>Filtrar por:</strong></p>
              <Select
                value={downloadType}
                onChange={(val) => {
                  setDownloadType(val);
                  // Limpa as datas ao trocar o tipo para evitar confusão
                  setDownloadRangeVenda(null);
                  setDownloadRangePgto(null);
                  // Previsão de Pagamento já vem com a janela padrão (amanhã -> +30 dias).
                  setDownloadRangePrevisao(val === "previsao" ? previsaoRangePadrao() : null);
                }}
                style={{ width: '100%' }}
              >
                <Option value="venda">Data de Venda</Option>
                <Option value="pagamento">Data de Pagamento</Option>
                <Option value="previsao">Previsão de Pagamento</Option>
                <Option value="atraso">Total em Atraso</Option>
              </Select>
            </div>

            {downloadType === "atraso" ? (
              <div>
                <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
                  Serão exportadas todas as parcelas em atraso (em aberto com previsão de pagamento
                  vencida), sem seleção de período.
                </p>
              </div>
            ) : (
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>
                    Selecione o período de {
                      downloadType === "venda" ? "Venda"
                        : downloadType === "previsao" ? "Previsão de Pagamento"
                          : "Pagamento"
                    }:
                  </strong>
                </p>

                {downloadType === "venda" ? (
                  <RangePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    locale={ptBR.DatePicker}
                    value={downloadRangeVenda}
                    onChange={(dates) => setDownloadRangeVenda(dates as [any, any])}
                    placeholder={['Início Venda', 'Fim Venda']}
                  />
                ) : downloadType === "previsao" ? (
                  <RangePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    locale={ptBR.DatePicker}
                    value={downloadRangePrevisao}
                    onChange={(dates) => setDownloadRangePrevisao(dates as [any, any])}
                    placeholder={['Início Previsão', 'Fim Previsão']}
                  />
                ) : (
                  <RangePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    locale={ptBR.DatePicker}
                    value={downloadRangePgto}
                    onChange={(dates) => setDownloadRangePgto(dates as [any, any])}
                    placeholder={['Início Pagamento', 'Fim Pagamento']}
                  />
                )}
              </div>
            )}
          </div>
        </Modal>
        <S.SummaryCardsContainer>
          <S.SummaryCard color="#4a4a4a">
            <div className="card-label">TOTAL BRUTO</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.valorBruto)}</div>
            <div className="card-subvalue"><strong>{formatSummaryCount(totalItems)}</strong> vendas</div>
          </S.SummaryCard>
          <S.SummaryCard color="#e67e22">
            <div className="card-label">TOTAL DESCONTO</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.totalDesconto)}</div>
          </S.SummaryCard>
          <S.SummaryCard color="#1890ff">
            <div className="card-label">TOTAL LÍQUIDO</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.totalLiquido)}</div>
          </S.SummaryCard>
          <S.SummaryCard color="#27ae60">
            <div className="card-label">TOTAL PAGO</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.totalPago)}</div>
          </S.SummaryCard>
          <S.SummaryCard color="#f39c12">
            <div className="card-label">A RECEBER</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.totalAReceber)}</div>
          </S.SummaryCard>
          <S.SummaryCard
            color="#e74c3c"
            onClick={() => setSomenteAtraso(prev => !prev)}
            style={{ cursor: 'pointer' }}
            title={somenteAtraso
              ? 'Clique para remover o filtro de atraso'
              : 'Clique para listar todos os lançamentos em atraso'}
          >
            <div className="card-label">TOTAL EM ATRASO{somenteAtraso ? ' (FILTRADO)' : ''}</div>
            <div className="card-value">{formatSummaryCurrency(summaryTotals.totalAtraso)}</div>
          </S.SummaryCard>
        </S.SummaryCardsContainer>

        <Table
          key={tableKey}
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
          onChange={(_, filters) => {
            setColumnFilters(filters);
          }}
        />

        <Modal
          title={vendaSelecionada
            ? `Parcelas da venda — ${vendaSelecionada.adquirente || ''} • NSU/CV ${vendaSelecionada.nsucv || '-'} • Aut. ${vendaSelecionada.codigoDeAutorizacao || '-'}`
            : 'Parcelas da venda'}
          visible={!!vendaSelecionada}
          onCancel={() => setVendaSelecionada(null)}
          footer={null}
          centered
          width="90vw"
          style={{ maxWidth: 1750 }}
          bodyStyle={{ height: '50vh', overflow: 'hidden', padding: 16 }}
        >
          <S.ModalParcelas>
          <Table
            columns={parcelaColumns}
            dataSource={vendaSelecionada?.parcelas ?? []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 300 }}
            summary={(pageData) => {
              const parse = (v: any) => {
                const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
                return Number.isFinite(n) ? n : 0;
              };
              let bruto = 0, desconto = 0, liquido = 0;
              pageData.forEach((r) => {
                bruto += parse(r.valorBruto);
                desconto += parse(r.desconto);
                liquido += parse(r.valorLiquido);
              });
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
                    <Table.Summary.Cell index={0}>{`Total (${pageData.length})`}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{formatCurrency(bruto)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{formatCurrency(desconto)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4}>{formatCurrency(liquido)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} />
                    <Table.Summary.Cell index={6} />
                    <Table.Summary.Cell index={7} />
                    <Table.Summary.Cell index={8} />
                    <Table.Summary.Cell index={9} />
                    <Table.Summary.Cell index={10} />
                    <Table.Summary.Cell index={11} />
                    <Table.Summary.Cell index={12} />
                    <Table.Summary.Cell index={13} />
                    <Table.Summary.Cell index={14} />
                    <Table.Summary.Cell index={15} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          </S.ModalParcelas>
        </Modal>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination
            current={currentPage}
            pageSize={50}
            total={totalItems}
            onChange={p => fetchData(p)}
            showSizeChanger={false}
          />
        </div>
      </S.DivTable>
    </S.Container>
  );
};

export default TabelaConciliadorFinanceira;